import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useQuery } from 'react-query';
import Layout from '../components/Layout';
import { Calendar, momentLocalizer, Views } from 'react-big-calendar';
import moment from 'moment';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { usePractice } from '../context/PracticeContext';
import axios from '../utils/axiosInstance';

// API base URL from environment variable
const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

// Setup moment locale
moment.locale('de', {
  months : [
    "Januar", "Februar", "März", "April", "Mai", "Juni", "Juli",
    "August", "September", "Oktober", "November", "Dezember"
  ],
  monthsShort : [
    "Jan", "Feb", "Mär", "Apr", "Mai", "Jun",
    "Jul", "Aug", "Sep", "Okt", "Nov", "Dez"
  ],
  weekdays : [
    "Sonntag", "Montag", "Dienstag", "Mittwoch", "Donnerstag", "Freitag", "Samstag"
  ],
  weekdaysShort : ["So", "Mo", "Di", "Mi", "Do", "Fr", "Sa"],
  weekdaysMin : ["So", "Mo", "Di", "Mi", "Do", "Fr", "Sa"],
  longDateFormat : {
    LT : "HH:mm",
    LTS : "HH:mm:ss",
    L : "DD.MM.YYYY",
    LL : "D. MMMM YYYY",
    LLL : "D. MMMM YYYY HH:mm",
    LLLL : "dddd, D. MMMM YYYY HH:mm"
  },
  calendar : {
    sameDay : '[Heute um] LT',
    nextDay : '[Morgen um] LT',
    nextWeek : 'dddd [um] LT',
    lastDay : '[Gestern um] LT',
    lastWeek : '[letzten] dddd [um] LT',
    sameElse : 'L'
  },
  relativeTime : {
    future : "in %s",
    past : "vor %s",
    s : "wenigen Sekunden",
    ss : "%d Sekunden",
    m : "einer Minute",
    mm : "%d Minuten",
    h : "einer Stunde",
    hh : "%d Stunden",
    d : "einem Tag",
    dd : "%d Tagen",
    M : "einem Monat",
    MM : "%d Monaten",
    y : "einem Jahr",
    yy : "%d Jahren"
  },
  dayOfMonthOrdinalParse: /\d{1,2}\./,
  ordinal : '%d.',
  week : {
    dow : 1, // Monday is the first day of the week.
    doy : 4  // The week that contains Jan 4th is the first week of the year.
  }
});

const localizer = momentLocalizer(moment);

// German messages for react-big-calendar
const messages = {
  allDay: 'Ganztägig',
  previous: 'Zurück',
  next: 'Weiter',
  today: 'Heute',
  month: 'Monat',
  week: 'Woche',
  day: 'Tag',
  agenda: 'Agenda',
  date: 'Datum',
  time: 'Zeit',
  event: 'Termin',
  noEventsInRange: 'Keine Termine in diesem Zeitraum.',
  showMore: total => `+ ${total} weitere`
};

// Add custom styles for calendar vertical spacing and controls
const calendarCustomStyles = `
  .rbc-time-content > * > .rbc-timeslot-group {
    min-height: 36px !important; /* Increase vertical spacing for time slots */
  }
  .calendar-controls-row {
    margin-bottom: 1.5rem;
  }
  @media (max-width: 700px) {
    .calendar-controls-row { flex-direction: column; align-items: stretch; }
    .calendar-controls.card { margin-left: 0 !important; margin-top: 1rem; }
  }
`;

// Helper function to convert DD.MM.YYYY to YYYY-MM-DD for HTML date inputs
const convertToInputFormat = (dateString) => {
  if (!dateString) return '';
  
  // Handle DD.MM.YYYY format (from database)
  const ddmmyyyy = dateString.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
  if (ddmmyyyy) {
    const [, day, month, year] = ddmmyyyy;
    return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
  }
  
  // If already in YYYY-MM-DD format, return as is
  if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
    return dateString;
  }
  
  return '';
};

const CalendarPage = () => {
  // Session 10d: practiceId comes from PracticeContext — replaces the
  // keycloak.sub → userId → practiceId resolution chain that previously lived here.
  const { practiceId, loading: practiceLoading } = usePractice();
  const [events, setEvents] = useState([]);
  const [blockLength, setBlockLength] = useState(60);
  const [originalBlockLength, setOriginalBlockLength] = useState(60);
  const [blockLengthSaving, setBlockLengthSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [newEvent, setNewEvent] = useState({
    title: '',
    start: new Date(),
    end: new Date(new Date().setMinutes(new Date().getMinutes() + 15)),
    patientName: '',
    patientPhone: '',
    patientEmail: '',
    patientBirthdate: '',
    notes: '',
    blocks: 1
  });
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  // Track calendar view and date for custom navigation logic
  const [currentView, setCurrentView] = useState(Views.WEEK);
  const [currentDate, setCurrentDate] = useState(new Date());
  const birthdateRef = useRef(null);
  const startDateRef = useRef(null);
  
  // --- Dynamic CSS Calculation ---
  const desiredHourHeight = 100; // px - adjust for more/less vertical space per hour
  let calendarStepProp = blockLength;
  let calendarTimeslotsProp;

  if (blockLength === 45) {
    calendarTimeslotsProp = 4; // Use a 15-minute visual grid (4 slots per hour)
  } else {
    // Ensure blockLength is a divisor of 60 for simplicity here, or add more robust handling
    calendarTimeslotsProp = (blockLength > 0 && 60 % blockLength === 0) ? (60 / blockLength) : 4; // Default to 4 if not a clean divisor
  }
  const individualSegmentHeight = desiredHourHeight / calendarTimeslotsProp;

  // Session 10d: practice resolution moved to PracticeContext.
  // Surface a friendly error and stop the spinner when the context settles
  // with no accessible practice — otherwise isLoading stays true forever and
  // the error message is unreachable.
  useEffect(() => {
    if (!practiceLoading && !practiceId) {
      setErrorMessage('Keine Praxis gefunden. Bitte konfigurieren Sie Ihre Praxis in den Einstellungen.');
      setIsLoading(false);
    } else if (practiceId) {
      setErrorMessage('');
    }
  }, [practiceLoading, practiceId]);

  // Fetch appointments from backend (cached via React Query).
  // Demo-level cache wrap: the query owns the network fetch + formatting and
  // hydrates the local `events` state via onSuccess, so all the existing
  // setEvents-based add/edit/delete mutation handlers keep working unchanged.
  // Key includes blockLength because `blocks` is derived from it.
  const loadFromLocalStorage = useCallback(() => {
    console.log('Attempting to load from localStorage as fallback');
    const savedEvents = localStorage.getItem(`calendarEvents-${practiceId}`);
    if (savedEvents) {
      try {
        const parsedEvents = JSON.parse(savedEvents);
        // Convert string dates back to Date objects
        const eventsWithDates = parsedEvents.map(event => ({
          ...event,
          start: new Date(event.start),
          end: new Date(event.end)
        }));
        setEvents(eventsWithDates);
      } catch (error) {
        console.error('Error parsing stored events:', error);
      }
    }
  }, [practiceId]);

  const { data: appointmentsData, isFetching: appointmentsFetching } = useQuery(
    ['appointments', practiceId, blockLength],
    async () => {
      // Ensure practiceId is properly formatted as a UUID
      const formattedPracticeId = practiceId.replace(/[^0-9a-f-]/g, '');
      const response = await axios.get(`${API_BASE_URL}/appointments/practice/${formattedPracticeId}`);
      console.log('Appointments loaded:', response.data);

      // Convert the API data to calendar events format
      return response.data
        .filter(appointment => appointment.status !== 'cancelled')
        .map(appointment => ({
          id: appointment.id,
          title: `Termin - ${appointment.patient_name}`,
          start: new Date(appointment.start_time),
          end: new Date(appointment.end_time),
          patientName: appointment.patient_name,
          patientPhone: appointment.patient_phone,
          patientEmail: appointment.patient_email || '',
          patientBirthdate: convertToInputFormat(appointment.patient_birthdate || ''),
          notes: appointment.notes || '',
          status: appointment.status,
          blocks: Math.ceil((new Date(appointment.end_time) - new Date(appointment.start_time)) / (blockLength * 60 * 1000))
        }));
    },
    {
      enabled: !!practiceId,
      onSuccess: () => {
        setErrorMessage('');
      },
      onError: (error) => {
        console.error('Error loading appointments:', error);
        // If 404, this is a new practice without appointments yet - that's OK
        if (error.response && error.response.status === 404) {
          console.log('No appointments found for practice, starting with empty calendar');
          setEvents([]);
        } else {
          setErrorMessage('Termine konnten nicht geladen werden. Bitte versuchen Sie es später erneut.');
          // Load from localStorage as a fallback
          loadFromLocalStorage();
        }
      },
    }
  );

  // Hydrate events from the query cache on every mount, including a cached remount
  // within staleTime (react-query v3 onSuccess does not fire on a cache hit), so a
  // revisit paints instantly instead of rendering an empty calendar.
  useEffect(() => {
    if (appointmentsData) {
      setEvents(appointmentsData);
    }
  }, [appointmentsData]);

  // Mirror the query's fetch state onto the existing isLoading flag, but only
  // when there's no cached data yet (so a cached revisit paints instantly
  // instead of flashing the loading spinner).
  useEffect(() => {
    if (!practiceId) return;
    setIsLoading(appointmentsFetching && events.length === 0);
  }, [appointmentsFetching, events.length, practiceId]);
  
  // Fetch appointment length when practice is available
  useEffect(() => {
    if (practiceId) {
      fetchAppointmentLength();
    }
  }, [practiceId]);
  
  // Recalculate newEvent.end and newEvent.blocks when start time or blockLength changes
  useEffect(() => {
    const startTime = new Date(newEvent.start);
    const numBlocks = newEvent.blocks || 1;
    const endTime = new Date(startTime.getTime() + (numBlocks * blockLength * 60 * 1000));
    setNewEvent(prev => ({ ...prev, end: endTime }));
  }, [newEvent.start, newEvent.blocks, blockLength]);
  
  const handleSelectSlot = ({ start, end, action }) => {
    if (action === 'click' && currentView === Views.MONTH) {
      setCurrentView(Views.DAY);
      setCurrentDate(start);
      return;
    }
    // For 'select' or 'doubleClick' or click in day/week view
    if (action === 'select' || action === 'doubleClick' || (action === 'click' && currentView !== Views.MONTH)) {
      setSelectedEvent(null);
      const numBlocks = 1; // Default to 1 block when creating
      setNewEvent({
        title: '',
        start: start,
        end: new Date(start.getTime() + numBlocks * blockLength * 60000),
        patientName: '',
        patientPhone: '',
        patientEmail: '',
        notes: '',
        blocks: numBlocks
      });
      setShowForm(true);
    }
  };

  const handleSelectEvent = (event) => {
    setSelectedEvent(event);
    const durationMinutes = (event.end.getTime() - event.start.getTime()) / 60000;
    const numBlocks = Math.max(1, Math.round(durationMinutes / blockLength));
    setNewEvent({
      title: event.title.replace('Termin - ', ''), // Show patient name directly
      start: event.start,
      end: event.end,
      patientName: event.patientName,
      patientPhone: event.patientPhone,
      patientEmail: event.patientEmail || '',
      patientBirthdate: event.patientBirthdate || '', // Already converted to input format
      notes: event.notes,
      blocks: numBlocks
    });
    setShowForm(true);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    let processedValue = value;
    if (name === 'blocks') {
      processedValue = parseInt(value, 10) || 1;
      const startTime = new Date(newEvent.start);
      const endTime = new Date(startTime.getTime() + (processedValue * blockLength * 60 * 1000));
      setNewEvent(prev => ({ ...prev, [name]: processedValue, end: endTime }));
    } else if (name === 'patientName') {
      setNewEvent(prev => ({ ...prev, [name]: processedValue, title: processedValue ? `Termin - ${processedValue}`: '' }));
    } else {
      setNewEvent(prev => ({ ...prev, [name]: processedValue }));
    }
  };

  const handleBlockLengthChange = (e) => {
    const newBlockLength = parseInt(e.target.value, 10);
    setBlockLength(newBlockLength);
    // newEvent.blocks will be recalculated by the useEffect dependency on blockLength
  };

  const fetchAppointmentLength = async () => {
    if (!practiceId) return;
    
    try {
      const response = await axios.get(`${API_BASE_URL}/practices/${practiceId}/appointment-length`);
      const length = response.data.default_appointment_length || 15;
      setBlockLength(length);
      setOriginalBlockLength(length);
    } catch (error) {
      console.error('Error fetching appointment length:', error);
      // Keep default value of 15 if fetch fails
    }
  };

  const saveAppointmentLength = async () => {
    if (!practiceId) return;
    
    setBlockLengthSaving(true);
    try {
      await axios.put(`${API_BASE_URL}/practices/${practiceId}/appointment-length`, {
        default_appointment_length: blockLength
      });
      setOriginalBlockLength(blockLength);
      setErrorMessage('');
    } catch (error) {
      console.error('Error saving appointment length:', error);
      setErrorMessage('Fehler beim Speichern der Terminlänge. Bitte versuchen Sie es erneut.');
    } finally {
      setBlockLengthSaving(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!practiceId) {
      setErrorMessage('Konnte Termin nicht speichern: Keine Praxis gefunden.');
      return;
    }
    
    // Format dates to ISO string compatible with the backend's datetime.fromisoformat()
    const formatDate = (date) => date.toISOString();
    
    // Prepare appointment data for API. Backend requires patient_phone to be
    // non-null, so if the user leaves it empty we send a single space.
    const appointmentData = {
      patient_name: newEvent.patientName,
      patient_phone: newEvent.patientPhone?.trim() || ' ',
      patient_email: newEvent.patientEmail || null,
      patient_birthdate: newEvent.patientBirthdate || null,
      start_time: formatDate(newEvent.start),
      end_time: formatDate(newEvent.end),
      notes: newEvent.notes,
      status: "scheduled"
    };
    
    try {
      let updatedEvent;
      
      if (selectedEvent) {
        // Update existing appointment
        const response = await axios.put(`${API_BASE_URL}/appointments/${selectedEvent.id}`, appointmentData);
        console.log('Appointment updated:', response.data);
        
        updatedEvent = {
          id: response.data.id,
          title: `Termin - ${response.data.patient_name}`,
          start: new Date(response.data.start_time),
          end: new Date(response.data.end_time),
          patientName: response.data.patient_name,
          patientPhone: response.data.patient_phone,
          patientEmail: response.data.patient_email || '',
          patientBirthdate: convertToInputFormat(response.data.patient_birthdate || ''),
          notes: response.data.notes || '',
          status: response.data.status,
          blocks: Math.ceil((new Date(response.data.end_time) - new Date(response.data.start_time)) / (blockLength * 60 * 1000))
        };
        
        // Update the events state
        setEvents(events.map(event => 
          event.id === selectedEvent.id ? updatedEvent : event
        ));
      } else {
        // Create new appointment
        console.log('Creating appointment with practice_id:', practiceId);
        console.log('Appointment data:', appointmentData);
        const requestData = {
          ...appointmentData,
          practice_id: practiceId
        };
        console.log('Full request payload:', requestData);
        try {
          const response = await axios.post(`${API_BASE_URL}/appointments/`, requestData);
          console.log('Server response:', response.data);
          
          updatedEvent = {
            id: response.data.id,
            title: `Termin - ${response.data.patient_name}`,
            start: new Date(response.data.start_time),
            end: new Date(response.data.end_time),
            patientName: response.data.patient_name,
            patientPhone: response.data.patient_phone,
            patientEmail: response.data.patient_email || '',
            patientBirthdate: convertToInputFormat(response.data.patient_birthdate || ''),
            notes: response.data.notes || '',
            status: response.data.status,
            blocks: Math.ceil((new Date(response.data.end_time) - new Date(response.data.start_time)) / (blockLength * 60 * 1000))
          };
          
          // Add to events state
          setEvents([...events, updatedEvent]);
        } catch (error) {
          console.error('Error saving appointment:', error);
          setErrorMessage('Fehler beim Speichern des Termins. Bitte versuchen Sie es erneut.');
        }
      }
      
      setErrorMessage('');
      
      // Also save to localStorage as a backup
      setTimeout(() => {
        const updatedEvents = selectedEvent 
          ? events.map(event => event.id === selectedEvent.id ? updatedEvent : event)
          : [...events, updatedEvent];
        localStorage.setItem(`calendarEvents-${practiceId}`, JSON.stringify(updatedEvents));
      }, 0);
      
    } catch (error) {
      console.error('Error saving appointment:', error);
      setErrorMessage('Fehler beim Speichern des Termins. Bitte versuchen Sie es erneut.');
    }
    
    setShowForm(false);
    setSelectedEvent(null);
    setNewEvent({
      title: '',
      start: new Date(),
      end: new Date(new Date().setMinutes(new Date().getMinutes() + blockLength)),
      patientName: '',
      patientPhone: '',
      patientEmail: '',
      patientBirthdate: '',
      notes: '',
      blocks: 1
    });
  };

  const handleCancel = () => {
    setShowForm(false);
    setSelectedEvent(null);
  };

  // Close the new/edit-event modal on Esc.
  useEffect(() => {
    if (!showForm) return;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        handleCancel();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [showForm]);

  const handleDelete = async () => {
    if (selectedEvent && practiceId) {
      try {
        await axios.delete(`${API_BASE_URL}/appointments/${selectedEvent.id}`);
        console.log('Appointment deleted');
        
        // Update state
        const updatedEvents = events.filter(event => event.id !== selectedEvent.id);
        setEvents(updatedEvents);
        
        // Update localStorage backup
        localStorage.setItem(`calendarEvents-${practiceId}`, JSON.stringify(updatedEvents));
        
        setErrorMessage('');
      } catch (error) {
        console.error('Error deleting appointment:', error);
        setErrorMessage('Fehler beim Löschen des Termins. Bitte versuchen Sie es später erneut.');
      }
      
      setShowForm(false);
      setSelectedEvent(null);
    }
  };

  // Format dates using European format
  const formats = {
    dateFormat: 'DD.MM.YYYY',
    dayFormat: 'ddd DD.MM',
    monthHeaderFormat: 'MMMM YYYY',
    dayHeaderFormat: 'dddd DD.MM.YYYY',
    dayRangeHeaderFormat: ({ start, end }) => 
      `${moment(start).format('DD.MM.YYYY')} - ${moment(end).format('DD.MM.YYYY')}`,
    timeGutterFormat: 'HH:mm',
    eventTimeRangeFormat: ({ start, end }) => 
      `${moment(start).format('HH:mm')} - ${moment(end).format('HH:mm')}`,
  };

  // Scroll to 8 AM
  const eightAM = new Date();
  eightAM.setHours(8, 0, 0, 0);

  // Render-time guard: wait for PracticeContext to resolve before rendering.
  if (practiceLoading) {
    return <Layout><div className="loading">Laden...</div></Layout>;
  }

  return (
    <html lang="de">
      <Layout>
        <style>{calendarCustomStyles}</style>
        <div className="calendar-page">
          <h1 className="h1">Terminkalender</h1>
          <div style={{ marginBottom: '1.5rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <label className="form-label" style={{ margin: 0, minWidth: 'max-content' }}>Terminblock-Länge (Minuten)</label>
                <select 
                  className="form-input" 
                  value={blockLength} 
                  onChange={handleBlockLengthChange}
                  style={{ display: 'inline-block', width: 'auto', minWidth: 80 }}
                >
                  <option value="5">5 Minuten</option>
                  <option value="10">10 Minuten</option>
                  <option value="15">15 Minuten</option>
                  <option value="20">20 Minuten</option>
                  <option value="30">30 Minuten</option>
                  <option value="45">45 Minuten</option>
                  <option value="60">60 Minuten</option>
                </select>
                {blockLength !== originalBlockLength && (
                  <button 
                    type="button"
                    className="button"
                    onClick={saveAppointmentLength}
                    disabled={blockLengthSaving}
                    style={{ 
                      padding: '4px 12px', 
                      fontSize: '12px',
                      backgroundColor: 'var(--color-secondary)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: blockLengthSaving ? 'not-allowed' : 'pointer',
                      marginLeft: '8px'
                    }}
                  >
                    {blockLengthSaving ? 'Speichern...' : 'Speichern'}
                  </button>
                )}
            </div>
          </div>
          {errorMessage && (
            <div className="error-message">{errorMessage}</div>
          )}
          {isLoading ? (
            <div className="loading">Termine werden geladen...</div>
          ) : (
            <div className="calendar-container card" style={{ padding: 0, boxShadow: 'none', border: 'none' }}>
              <Calendar
                localizer={localizer}
                events={events}
                startAccessor="start"
                endAccessor="end"
                onSelectEvent={handleSelectEvent}
                onSelectSlot={handleSelectSlot}
                selectable
                style={{ height: 600, background: '#fff', borderRadius: 8 }}
                formats={formats}
                messages={messages}
                view={currentView}
                date={currentDate}
                onView={view => setCurrentView(view)}
                onNavigate={date => setCurrentDate(date)}
                scrollToTime={eightAM}
                step={calendarStepProp}
                timeslots={calendarTimeslotsProp}
                defaultView={Views.WEEK}
                defaultDate={new Date()}
                popup
              />
            </div>
          )}
          {showForm && (
            <div className="event-form-container">
              <div className="card event-form">
                <h3 className="h3">{selectedEvent ? 'Termin bearbeiten' : 'Neuer Termin'}</h3>
                <form onSubmit={handleSubmit}>
                  <div className="form-group">
                    <label className="form-label">Patientenname</label>
                    <input
                      type="text"
                      name="patientName"
                      value={newEvent.patientName}
                      onChange={handleInputChange}
                      className="form-input"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Telefonnummer</label>
                    <input
                      type="text"
                      name="patientPhone"
                      value={newEvent.patientPhone}
                      onChange={handleInputChange}
                      className="form-input"
                      placeholder="optional"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">E-Mail-Adresse</label>
                    <input
                      type="email"
                      name="patientEmail"
                      value={newEvent.patientEmail}
                      onChange={handleInputChange}
                      className="form-input"
                      placeholder="patient@beispiel.de (optional)"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Patienten-Geburtsdatum</label>
                    <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                      <input
                        type="text"
                        readOnly
                        value={newEvent.patientBirthdate ? moment(newEvent.patientBirthdate).format('DD.MM.YYYY') : ''}
                        className="form-input"
                        onClick={() => birthdateRef.current && birthdateRef.current.showPicker()}
                        style={{ cursor: 'pointer' }}
                      />
                      <span
                        style={{
                          position: 'absolute',
                          right: '12px',
                          cursor: 'pointer',
                          fontSize: '18px',
                          pointerEvents: 'none'
                        }}
                      >
                        📅
                      </span>
                      <input
                        type="date"
                        ref={birthdateRef}
                        name="patientBirthdate"
                        value={newEvent.patientBirthdate}
                        onChange={handleInputChange}
                        className="form-input"
                        lang="de"
                        title="Geburtsdatum auswählen"
                        style={{
                          opacity: 0,
                          position: 'absolute',
                          left: 0,
                          top: 0,
                          width: '100%',
                          height: '100%',
                          border: 'none',
                          cursor: 'pointer'
                        }}
                      />
                    </div>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Startzeit</label>
                    <div className="date-time-inputs">
                      <div style={{ position: 'relative', display: 'flex', alignItems: 'center', flexGrow: 1 }}>
                          <input
                            type="text"
                            readOnly
                            value={moment(newEvent.start).format('DD.MM.YYYY')}
                            className="form-input date-input"
                            onClick={() => startDateRef.current && startDateRef.current.showPicker()}
                            style={{ cursor: 'pointer' }}
                          />
                          <span
                            style={{
                              position: 'absolute',
                              right: '12px',
                              cursor: 'pointer',
                              fontSize: '18px',
                              pointerEvents: 'none'
                            }}
                          >
                            📅
                          </span>
                          <input
                            type="date"
                            ref={startDateRef}
                            name="start-date"
                            value={moment(newEvent.start).format('YYYY-MM-DD')}
                            onChange={(e) => {
                              const date = e.target.value;
                              const time = moment(newEvent.start).format('HH:mm');
                              const newStart = moment(`${date}T${time}`).toDate();
                              setNewEvent({
                                ...newEvent,
                                start: newStart,
                                end: new Date(newStart.getTime() + newEvent.blocks * blockLength * 60 * 1000)
                              });
                            }}
                            className="form-input date-input"
                            required
                            lang="de"
                            title="Startdatum auswählen"
                            style={{
                              opacity: 0,
                              position: 'absolute',
                              left: 0,
                              top: 0,
                              width: '100%',
                              height: '100%',
                              border: 'none',
                              cursor: 'pointer'
                            }}
                          />
                      </div>
                      <div className="time-selects">
                        <select 
                          name="start-hour"
                          value={moment(newEvent.start).format('HH')}
                          onChange={(e) => {
                            const hour = e.target.value;
                            const minute = moment(newEvent.start).format('mm');
                            const date = moment(newEvent.start).format('YYYY-MM-DD');
                            const newStart = moment(`${date}T${hour}:${minute}`).toDate();
                            setNewEvent({
                              ...newEvent,
                              start: newStart,
                              end: new Date(newStart.getTime() + newEvent.blocks * blockLength * 60000)
                            });
                          }}
                          className="time-select hour-select"
                          required
                        >
                          {Array.from({ length: 24 }, (_, i) => {
                            const hour = i.toString().padStart(2, '0');
                            return <option key={hour} value={hour}>{hour}</option>;
                          })}
                        </select>
                        <span>:</span>
                        <select 
                          name="start-minute"
                          value={moment(newEvent.start).format('mm')}
                          onChange={(e) => {
                            const hour = moment(newEvent.start).format('HH');
                            const minute = e.target.value;
                            const date = moment(newEvent.start).format('YYYY-MM-DD');
                            const newStart = moment(`${date}T${hour}:${minute}`).toDate();
                            setNewEvent({
                              ...newEvent,
                              start: newStart,
                              end: new Date(newStart.getTime() + newEvent.blocks * blockLength * 60000)
                            });
                          }}
                          className="time-select minute-select"
                          required
                        >
                          {Array.from({ length: 12 }, (_, i) => {
                            const minute = (i * 5).toString().padStart(2, '0');
                            return <option key={minute} value={minute}>{minute}</option>;
                          })}
                        </select>
                      </div>
                    </div>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Anzahl Blöcke ({blockLength} Min. je Block)</label>
                    <input
                      type="number"
                      name="blocks"
                      min="1"
                      value={newEvent.blocks}
                      onChange={handleInputChange}
                      className="form-input"
                      required
                    />
                    <small>Termin endet um: {moment(newEvent.end).format('DD.MM.YYYY HH:mm')}</small>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Notizen</label>
                    <textarea
                      name="notes"
                      value={newEvent.notes}
                      onChange={handleInputChange}
                      className="form-input"
                      rows="3"
                    />
                  </div>
                  <div className="form-actions">
                    <button type="submit" className="button">
                      {selectedEvent ? 'Aktualisieren' : 'Speichern'}
                    </button>
                    {selectedEvent && (
                      <button 
                        type="button" 
                        className="button delete-button"
                        onClick={handleDelete}
                      >
                        Löschen
                      </button>
                    )}
                    <button 
                      type="button" 
                      className="button cancel-button"
                      onClick={handleCancel}
                    >
                      Abbrechen
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </div>
      </Layout>
    </html>
  );
};

export default CalendarPage; 